import {
    getAfterRotatePoints,
    getFramePointsOnSlide,
    getFrameWithPoints,
} from '@src/slide/application/service/snap-line-service/util/frame';
import type { Vector } from '@src/slide/application/service/snap-line-service/util/vector';
import {
    calculateOtherSideLength,
    getVectorProjection,
} from '@src/slide/application/service/snap-line-service/util/vector';
import type {
    IAdsorbResult,
} from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import { OperationType } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import { Orient } from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import { isCloseEqual } from '@src/slide/application/service/snap-line-service/util';
import { shouldDisplayDebugContent } from '@src/slide/application/service/snap-line-service/util/debug';
import type { IActiveSnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';
import type { SnapLineContext } from '@src/slide/application/service/snap-line-service/context';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { isCornerAnchor } from '@src/slide/application/service/snap-line-service/util/movement';
import findLastIndex from 'lodash/findLastIndex';

/**
 *  元素的吸附器
 *  通过命中的参考线，根据edge距离、种类等多种因素，计算元素吸附后的位置，并更新元素的OperationFrame
 */
export class SnapLineAdsorber {
    public constructor(protected context: SnapLineContext) {}

    /**
     * 进行吸附
     */
    public adsorb(activeSnapLines: IActiveSnapGridLine[]): IAdsorbResult | undefined {
        const { operationType } = this.context;
        if (!activeSnapLines || activeSnapLines.length === 0) {
            return;
        }

        const adsorbSnapLines = this.filterSameOrientActiveSnapLines(activeSnapLines);
        const { framePatch, points, afterAdsorbFrame } = operationType === OperationType.RESIZE
            ? this.adsorbInResizing(adsorbSnapLines) : this.adsorbInMoving(adsorbSnapLines);
        const frameOnSlide = getFrameWithPoints(points);

        // 方便定位问题用
        this.displayAdsorbPointsInDevEnv(points);
        return { framePatch, frameOnSlide, afterAdsorbFrame };
    }

    private adsorbInMoving(snapLines: IActiveSnapGridLine[]): {
        framePatch: IFrame, points: IPoint[], afterAdsorbFrame: IFrame
    } {
        const framePatch = this.createInitialFramePatch();
        const points = getFramePointsOnSlide(this.context.nextSizeOnSlide, this.context.rotateOnSlide);
        // 方便定位问题用
        this.displayBeforeAdsorbPointsInDevEnv(points);

        const afterAdsorbFrame = { ...this.context.detectFrame };
        snapLines.forEach((snapLine) => {
            this.adsorbFrameWithActiveSnapLineInMoving(afterAdsorbFrame, snapLine);
            points.forEach((point) => {
                if (snapLine.orient === Orient.VERTICAL) {
                    point.x += snapLine.activeAdsorbGap;
                } else {
                    point.y += snapLine.activeAdsorbGap;
                }
            });
        });

        const { left, top } = this.getAdsorbFramePatch(points);
        framePatch.top += top;
        framePatch.left += left;

        return { framePatch, points, afterAdsorbFrame };
    }

    /**
     * 更新 moving 吸附后的 frame，用于 UI 展示吸附后的辅助线和吸附线
     */
    private adsorbFrameWithActiveSnapLineInMoving(frame: IFrame, snapLine: IActiveSnapGridLine) {
        if (snapLine.orient === Orient.VERTICAL) {
            frame.left += snapLine.activeAdsorbGap;
        } else {
            frame.top += snapLine.activeAdsorbGap;
        }
    }

    private adsorbInResizing(snapLines: IActiveSnapGridLine[]): {
        framePatch: IFrame, points: IPoint[], afterAdsorbFrame: IFrame
    } {
        const framePatch = this.createInitialFramePatch();
        const detectFrameOffsetFrame: IFrame = { left: 0, top: 0, width: 0, height: 0 };
        let points = getFramePointsOnSlide(this.context.nextSizeOnSlide, this.context.rotateOnSlide);

        // 方便定位问题用
        this.displayBeforeAdsorbPointsInDevEnv(points);

        snapLines.forEach((snapLine) => {
            this.adsorbFrameWithActiveSnapLineInResizing(detectFrameOffsetFrame, snapLine);

            // 确定两个对角的坐标
            const { staticPoint, afterAdsorbPoint, extendPoints } = this.getDiagonalAndExtendPoints(snapLine, points);
            if (!staticPoint || !afterAdsorbPoint) {
                return;
            }
            // 获取吸附后四个点的坐标
            points = this.getAfterAdsorbPoints(afterAdsorbPoint, staticPoint, extendPoints);
        });

        const { left, top, width, height } = this.getAdsorbFramePatch(points);
        framePatch.top += top;
        framePatch.left += left;
        framePatch.width += width;
        framePatch.height += height;

        const { detectFrame, operationAnchorOnSlide, elementScale, noChangeAspect } = this.context;
        const afterAdsorbFrame = { ...detectFrame };

        afterAdsorbFrame.top += detectFrameOffsetFrame.top;
        afterAdsorbFrame.left += detectFrameOffsetFrame.left;
        afterAdsorbFrame.width += detectFrameOffsetFrame.width;
        afterAdsorbFrame.height += detectFrameOffsetFrame.height;

        return { framePatch, points, afterAdsorbFrame };
    }

    /**
     * 更新 resizing 吸附后的 frame，用于 UI 展示吸附后的辅助线和吸附线
     */
    private adsorbFrameWithActiveSnapLineInResizing(frame: IFrame, snapLine: IActiveSnapGridLine) {
        const { operationAnchorOnSlide } = this.context;

        if (snapLine.orient === Orient.VERTICAL) {
            if (operationAnchorOnSlide.includes('left')) {
                frame.left += snapLine.activeAdsorbGap;
                frame.width -= snapLine.activeAdsorbGap;
            }
            if (operationAnchorOnSlide.includes('right')) {
                frame.width += snapLine.activeAdsorbGap;
            }
        } else {
            if (operationAnchorOnSlide.includes('top')) {
                frame.top += snapLine.activeAdsorbGap;
                frame.height -= snapLine.activeAdsorbGap;
            }
            if (operationAnchorOnSlide.includes('bottom')) {
                frame.height += snapLine.activeAdsorbGap;
            }
        }
    }


    /*
     * 变换结束后做 Patch计算
     * 根据变换后的点，推倒出变换后的 frame，与原 frame做 diff
     */
    private getAdsorbFramePatch(newPoints: IPoint[]) {
        // 将画布上的point根据在画布上的变换，转换为可以直接计算为frame的四个点
        const framePoints = this.getBeforeTransformPoints(
            newPoints,
            this.context.rotateOnSlide,
        );
        const newFrameOnSlide = getFrameWithPoints(framePoints);
        const newFrame = this.context.element.getFrame().convertFrameOnSlideToSelf({
            ...newFrameOnSlide,
            flipH: this.context.nextSizeOnSlide.flipH,
            flipV: this.context.nextSizeOnSlide.flipV,
            rotate: this.context.nextSizeOnSlide.rotate,
        });

        const { left, top, width, height } = newFrame;
        const leftPatch = left - this.context.nextSize.left;
        const topPatch = top - this.context.nextSize.top;
        const widthPatch = width - this.context.nextSize.width;
        const heightPatch =  height - this.context.nextSize.height;

        // patch在经过转换后，可能会有一定程度的精度问题，这里当足够小时视为 0
        return {
            left: isCloseEqual(leftPatch, 0) ? 0 : leftPatch,
            top: isCloseEqual(topPatch, 0) ? 0 : topPatch,
            width: isCloseEqual(widthPatch, 0) ? 0 : widthPatch,
            height: isCloseEqual(heightPatch, 0) ? 0 : heightPatch,
        };
    }

    /**
     * 根据吸附后的 Points，转换为rotate、flip之前的 frame的四个点，用于获取新的frame，来计算frame的变化
     */
    private getBeforeTransformPoints(newPoints: IPoint[], rotateOnSlide: number) {
        return getAfterRotatePoints(newPoints, -rotateOnSlide);
    }

    // 可能存在同一方向，有多根等 gap 的参考线。他们需要同时展示，但是实际的吸附只需要进行一次
    private filterSameOrientActiveSnapLines(activeSnapLines: IActiveSnapGridLine[]): IActiveSnapGridLine[] {
        const snapLineExisted = {
            [Orient.HORIZONTAL]: false,
            [Orient.VERTICAL]: false,
        };

        return activeSnapLines.filter((snapLine) => {
            if (!snapLineExisted[snapLine.orient]) {
                snapLineExisted[snapLine.orient] = true;
                return true;
            }
            return false;
        });
    }

    /*
     * 获取吸附点索引
     */
    // eslint-disable-next-line complexity
    private getAbsorbPointIndex(snapLine: IActiveSnapGridLine, originFramePoints: IPoint[]) {
        const beforeAdsorbPosition = snapLine.position - snapLine.activeAdsorbGap;
        const isAdsorbVertical = snapLine.orient === Orient.VERTICAL;

        const firstAdsorbPointIndex = originFramePoints
            .findIndex(p => isCloseEqual(isAdsorbVertical ? p.x : p.y, beforeAdsorbPosition));
        const lastAdsorbPointIndex = findLastIndex(
            originFramePoints,
            p => isCloseEqual(isAdsorbVertical ? p.x : p.y, beforeAdsorbPosition),
        );
        const firstPoint = originFramePoints[firstAdsorbPointIndex];
        const lastPoint = originFramePoints[lastAdsorbPointIndex];

        if (firstAdsorbPointIndex === lastAdsorbPointIndex) {
            return firstAdsorbPointIndex;
        }

        const { operationAnchorOnSlide } = this.context;
        if (isAdsorbVertical) {
            if (
                (operationAnchorOnSlide.includes('bottom') && firstPoint.y > lastPoint.y)
                || (operationAnchorOnSlide.includes('top') && firstPoint.y <= lastPoint.y)
            ) {
                return firstAdsorbPointIndex;
            }
            return lastAdsorbPointIndex;
        }

        if (
            (operationAnchorOnSlide.includes('right') && firstPoint.x >= lastPoint.x)
            || (operationAnchorOnSlide.includes('left') && firstPoint.x < lastPoint.x)
        ) {
            return firstAdsorbPointIndex;
        }
        return lastAdsorbPointIndex;
    }

    /**
     * 获取操作对角线的点，以及相邻的往外扩展的点。
     * 如：如果将矩形的右上角往右上方resize，则 staticPoint为左下角，afterAdsorbPoint为右上角。extendPoints为另外两个点
     */
    // eslint-disable-next-line complexity
    private getDiagonalAndExtendPoints(snapLine: IActiveSnapGridLine, originFramePoints: IPoint[]) {
        const isAdsorbVertical = snapLine.orient === Orient.VERTICAL;
        const adsorbPointIndex = this.getAbsorbPointIndex(snapLine, originFramePoints);
        if (adsorbPointIndex === -1) {
            return { staticPoint: null, extendPoints: [], afterAdsorbPoint: null };
        }

        // 吸附点
        const beforeAdsorbPoint = originFramePoints[adsorbPointIndex];
        // 对角点索引
        const oppositeStaticPointIndex = (adsorbPointIndex + 2) % 4;
        // 吸附点对角，是一个不会移动的静态点
        const staticPoint = originFramePoints[oppositeStaticPointIndex];
        const adsorbPatch = {
            x: isAdsorbVertical ? snapLine.activeAdsorbGap : 0,
            y: isAdsorbVertical ? 0 :  snapLine.activeAdsorbGap,
        };

        // 在锁定横纵比的情况调整吸附点位置，暂不打开维持当前逻辑，后续经过完善测试后打开
        // this.handleNoChangeAspectScale(adsorbPatch, beforeAdsorbPoint, staticPoint, snapLine);

        const afterAdsorbPoint = {
            x: beforeAdsorbPoint.x + adsorbPatch.x,
            y: beforeAdsorbPoint.y + adsorbPatch.y,
        };
        const extendPoints = originFramePoints
            .filter((p, index) => index !== oppositeStaticPointIndex && index !== adsorbPointIndex);

        return {
            staticPoint,
            afterAdsorbPoint,
            extendPoints,
        };
    }

    /**
     * 在锁定横纵比的情况调整吸附点位置
     */
    private handleNoChangeAspectScale(
        adsorbPatch: IPoint,
        beforeAdsorbPoint: IPoint,
        staticPoint: IPoint,
        snapLine: IActiveSnapGridLine,
    ) {
        const isAdsorbVertical = snapLine.orient === Orient.VERTICAL;
        const { noChangeAspect, originOperationAnchor } = this.context;
        if (noChangeAspect && isCornerAnchor(originOperationAnchor)) {
            const diagonalVector: Vector = [
                isCloseEqual(beforeAdsorbPoint.x, staticPoint.x) ? 0 : beforeAdsorbPoint.x - staticPoint.x,
                isCloseEqual(beforeAdsorbPoint.y, staticPoint.y) ? 0 : beforeAdsorbPoint.y - staticPoint.y,
            ];

            const vectorProductResult = diagonalVector[0] * diagonalVector[1];
            const scaleVector = vectorProductResult > 0 ? 1 : (vectorProductResult < 0 ? -1 : 0);
            const scalePatch = calculateOtherSideLength(snapLine.activeAdsorbGap, diagonalVector, isAdsorbVertical);

            if (isAdsorbVertical) {
                adsorbPatch.y = scalePatch * scaleVector;
            } else {
                adsorbPatch.x = scalePatch * scaleVector;
            }
        }
    }

    /**
     *  根据对角线向量和固定角的夹脚向量，计算出对角线在两边上的投影向量，从而计算出四个点的位置
     */
    private getAfterAdsorbPoints(afterAdsorbPoint: IPoint, staticPoint: IPoint, extendPoints: IPoint[]) {
        const diagonalVector: Vector = [
            afterAdsorbPoint.x - staticPoint.x,
            afterAdsorbPoint.y - staticPoint.y,
        ];
        const adjacentVectors: Vector[] = extendPoints.map(p => [p.x - staticPoint.x, p.y - staticPoint.y]);
        const afterExtendVectors = adjacentVectors.map(vector => getVectorProjection(diagonalVector, vector));
        const afterExtendPoints = afterExtendVectors
            .map(vector => ({ x: vector[0] + staticPoint.x, y: vector[1] + staticPoint.y }));

        return [
            staticPoint,
            afterExtendPoints[0],
            afterAdsorbPoint,
            afterExtendPoints[1],
        ];
    }

    private createInitialFramePatch(): IFrame {
        return { left: 0, top: 0, width: 0, height: 0 };
    }

    private get element() {
        return this.context.element;
    }

    private displayBeforeAdsorbPointsInDevEnv(points: IPoint[]) {
        if (shouldDisplayDebugContent()) {
            this.context.beforeAdsorbPoints = points.slice(0);
        }
    }

    private displayAdsorbPointsInDevEnv(points: IPoint[]) {
        if (shouldDisplayDebugContent()) {
            this.context.adsorbPoints = points.slice(0);
        }
    }
}
